import { apis } from '@src/shared/api';
import { logger } from '@src/shared/includes/logger';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { isFunction, isUndefined, merge } from 'lodash';

/**
 * Hook: useApiMutation
 *
 * Dynamic api request function based on mutation key to make on-demand api mutations.
 *
 * @param apiKey
 * @param mutationKey
 * @param options
 * @returns {*[]}
 */
export function useApiMutation(apiKey = 'Default', mutationKey = '', options = {}) {
  const { toast } = useToast();

  let mutationFunction;

  if (!isUndefined(apis?.[apiKey]?.mutations?.[mutationKey])) {
    mutationFunction = apis?.[apiKey]?.mutations?.[mutationKey];
  }

  const theMutation = merge(
    {},
    {
      excludedHttpCodes: [],
      mutationFn: async (variables) => {
        try {
          let response;

          if (isUndefined(mutationFunction)) {
            throw new Error(`Request not found for api key: ${apiKey} and mutation key: ${mutationKey}`);
          }

          if (isFunction(mutationFunction)) {
            response = await mutationFunction([apiKey, mutationKey, variables]);
          }

          return response;
        } catch (errorObject) {
          const error = errorObject?.error || errorObject;
          const excludedHttpCodes = options?.excludedHttpCodes || [];

          if (!excludedHttpCodes?.includes(error?.statusCode)) {
            toast.show({
              autoClose: false,
              id: `useApiMutation-${apiKey}-${mutationKey}`,
              message: error?.message || 'Unknown error',
              title: `API Error - ${mutationKey}`,
              variant: 'error',
            });
          }

          logger.error('src/app/context/Api/hooks/useApiMutation/useApiMutation.jsx -> useApiMutation', false, error);
          throw errorObject;
        }
      },
      mutationKey: [apiKey, mutationKey],
    },
    options
  );

  const mutation = useMutation(theMutation);

  return [mutation?.mutateAsync, mutation];
}
